import { Image, Link, mobile, styled, useIsMobile } from '@obvio/app'
import { Grid, GridItem, Stack } from '@obvio/ui'

import { CenteredSection } from '../CenteredSection'
import { CarouselBase, CarouselSection } from './Carousels/CarouselBase'

import type { ImageAsset } from '@obvio/app'
import type { AllowUndefinedForOptional } from '@obvio/utils'
import type { ReactElement } from 'react'

export type GridInfoElement = {
  id: string
  title: string
  image: ImageAsset
  href?: string | undefined
  hrefTitle?: string | undefined
  handleClick?(): void
}

type ElementCompProps = AllowUndefinedForOptional<{
  element: GridInfoElement
  onClick?(): void
}>

const ElementWrap = styled(Stack)<{ $clickable: boolean }>`
  color: ${(theme) => theme.colors.secondary};
  width: 100%;
  cursor: ${(_, { $clickable }) => ($clickable ? 'pointer' : 'default')};
`

const Wrap = styled(Grid)`
  @media ${mobile} {
    grid-template-columns: 1fr;
  }
`

const ImgWrap = styled.div`
  position: relative;
  height: 27.75rem;
  width: 100%;
  min-width: 21.1875rem;

  @media ${mobile} {
    min-width: 0;
  }
`

const CheckLink = styled(Link)`
  font-size: 1rem;
`

const Title = styled.span`
  font-size: 1.25rem;
  font-weight: 300;
  text-align: left;
`

const Divider = styled.div`
  height: 1px;
  width: 100%;
  background: ${(theme) => theme.stroke};
`

function ElementComp({ element, onClick }: ElementCompProps) {
  return (
    <ElementWrap
      $clickable={Boolean(onClick)}
      kind="vertical"
      spacing="big"
      onClick={() => onClick?.()}
    >
      <ImgWrap>
        <Image img={element.image} context={element.id} />
      </ImgWrap>
      <Divider />
      <Title>{element.title}</Title>
      {element.href ? (
        <CheckLink href={element.href}>{element.hrefTitle}</CheckLink>
      ) : null}
    </ElementWrap>
  )
}

type GridInfo3Props = {
  elements: GridInfoElement[]
}
export function GridInfo3({ elements }: GridInfo3Props): ReactElement {
  const isMobile = useIsMobile()
  if (isMobile) {
    return (
      <CarouselSection>
        <CarouselBase
          data={elements}
          render={(data) => <ElementComp element={data} />}
        />
      </CarouselSection>
    )
  }
  return (
    <CenteredSection>
      <Wrap gap="extraLarge" templateColumns="repeat(3, minmax(0,1fr))">
        {elements.map((element) => (
          <GridItem key={element.id}>
            <ElementComp element={element} onClick={element.handleClick} />
          </GridItem>
        ))}
      </Wrap>
    </CenteredSection>
  )
}
